import React, { useEffect } from "react";

import { CustomerInfo } from "shared/customer-info/CustomerInfo";
import { Grid, TextField, Typography, Card } from "@material-ui/core";
import { SelectOptions } from "common/assets/data/options";
import { SingleUserFieldForm } from "shared/single-user-field-form/SingleUserFieldForm";
import { StoreState } from "common/store/types";
import { useTranslation } from "react-i18next";
import { User } from "common/store/user/types";
import { useSelector } from "react-redux";

import {
  CommonTextField,
  CommonSlider,
  CommonCheckbox,
  CommonRadioGroup,
} from "common/components";

const availableCashOption = SelectOptions().increaseLimit.find(
  (option: any) => option.id === "availableCash"
);

const monthlySalaryOption = SelectOptions().increaseLimit.find(
  (option: any) => option.id === "monthlySalary"
);

export const SharesSubform = ({
  errors,
  setFieldValue,
  values,
  touched,
  property,
}: any) => {
  //Hooks
  const { t } = useTranslation();
  const storageUserDetails = useSelector<StoreState, User>(
    (state) => state.currentUser
  );

  //Actions
  useEffect(() => {
    const maxAmountPhysical = property.cap - property.total_sold;

    const maxAmountBase = Math.min(
      property.max,
      maxAmountPhysical,
      Math.floor(1000 / property.price)
    );

    let maxAmount = maxAmountBase;

    if (values.limitIncreased && availableCashOption && monthlySalaryOption) {
      switch (values.limitIncreasedBy) {
        case availableCashOption.value:
          if (storageUserDetails.availableCash) {
            maxAmount = Math.min(
              maxAmountPhysical,
              maxAmountBase + 0.1 * storageUserDetails.availableCash
            );
          }
          break;
        case monthlySalaryOption.value:
          if (storageUserDetails.monthlySalary) {
            maxAmount = Math.min(
              maxAmountPhysical,
              maxAmountBase + 2 * storageUserDetails.monthlySalary
            );
          }
          break;
        default:
          break;
      }
    }

    setFieldValue("maxAmount", maxAmount);
  }, [
    property.max,
    property.cap,
    property.total_sold,
    property.price,
    values.limitIncreased,
    values.limitIncreasedBy,
    storageUserDetails.availableCash,
    storageUserDetails.monthlySalary,
    setFieldValue,
  ]);

  useEffect(() => {
    if (values.amount > values.maxAmount) {
      setFieldValue("amount", values.maxAmount);
    }
  }, [values.maxAmount, values.amount, setFieldValue]);

  return (
    <Grid container direction="column" spacing={6}>
      <Grid item xs={12}>
        <TextField
          disabled
          label={t("Remaining shares")}
          value={`${property.cap - property.total_sold}/${property.cap}`}
          variant="outlined"
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>

      <Grid item container direction="column">
        <Grid item xs={12}>
          <CommonTextField
            type="number"
            id="amount"
            placeholder={t("Shares")}
            values={values}
            errors={errors}
            touched={touched}
            setFieldValue={(id: string, value: number) =>
              setFieldValue(id, Math.round(value))
            }
            inputProps={{
              min: values.minAmount,
              max: values.maxAmount,
            }}
          />
        </Grid>

        <Grid item xs={12} className="slider-wrapper">
          <CommonSlider
            id="amount"
            values={values}
            setFieldValue={setFieldValue}
            min={values.minAmount}
            max={values.maxAmount}
          />
        </Grid>
      </Grid>

      <Grid item container xs={12} className="section--nested">
        <Grid item xs={12}>
          <CommonCheckbox
            id="limitIncreased"
            values={values}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            label={t("I want to increase my limit")}
          />
        </Grid>

        {values.limitIncreased && (
          <Card className={"card--nested"}>
            <Grid item container xs={12} direction="column" spacing={3}>
              <Grid item xs={12}>
                <CommonRadioGroup
                  id="limitIncreasedBy"
                  values={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  label={t("Increase limit by")}
                  options={SelectOptions().increaseLimit}
                />
              </Grid>

              <Grid item xs={12}>
                {(availableCashOption &&
                  values.limitIncreasedBy === availableCashOption.value && (
                    <SingleUserFieldForm id="availableCash" />
                  )) ||
                  (monthlySalaryOption &&
                    values.limitIncreasedBy === monthlySalaryOption.value && (
                      <SingleUserFieldForm id="monthlySalary" />
                    ))}
              </Grid>
            </Grid>
          </Card>
        )}
      </Grid>

      <Grid justify="flex-start" container item>
        <CustomerInfo />
      </Grid>

      <Grid item className="section--nested" xs={12}>
        <Typography className="section-title--big" variant="subtitle1">
          {t("Summary")}
        </Typography>

        <TextField
          disabled
          label={t("Total")}
          value={`€ ${(1 * values.amount).toFixed(2)}`}
          variant="outlined"
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>
    </Grid>
  );
};
